import { useNavigate } from "react-router-dom";
import {
  Workflow as WorkflowIcon,
  Play,
  Calendar,
  Trash2,
  Phone,
  MessageSquare,
  Mail,
  MessageCircle,
  type LucideIcon,
} from "lucide-react";
import { ActionMenu } from "../../components/ui/action-menu";
import { cn } from "../../lib/utils";
import type { Workflow, WorkflowNode } from "./api";

interface AutomationCardProps {
  key?: string;
  workflow: Workflow;
  canEdit?: boolean;
  onDelete: (workflow: Workflow) => void;
}

interface ChannelItem {
  type: string;
  label: string;
  icon: LucideIcon;
}

const CHANNEL_DEFS: Record<string, Omit<ChannelItem, "type">> = {
  call: {
    label: "Voice",
    icon: Phone,
  },
  whatsapp: {
    label: "WhatsApp",
    icon: MessageSquare,
  },
  email: {
    label: "Email",
    icon: Mail,
  },
  sms: {
    label: "SMS",
    icon: MessageCircle,
  },
};

function getWorkflowChannels(nodes: WorkflowNode[]): ChannelItem[] {
  const types = new Set(nodes.map((n) => n.node_type || "call"));
  const list: ChannelItem[] = [];
  (["call", "whatsapp", "email", "sms"] as const).forEach((t) => {
    if (types.has(t)) {
      list.push({ type: t, ...CHANNEL_DEFS[t] });
    }
  });
  return list;
}

export function AutomationCard({
  workflow,
  canEdit = true,
  onDelete,
}: AutomationCardProps) {
  const navigate = useNavigate();

  const isPublished = workflow.status === "published";

  const nodes = workflow.nodes || [];
  const channels = getWorkflowChannels(nodes);
  const stepCount = nodes.length;

  const workflowName = workflow.name || "Untitled Workflow";
  const displayWorkflowName =
    workflowName.length > 45 ? workflowName.slice(0, 45) + "..." : workflowName;

  const openStudio = () => navigate(`/home/create-automation?id=${workflow.id}`);

  return (
    <article
      onClick={() => {
        if (canEdit) openStudio();
      }}
      onKeyDown={(event) => {
        if (!canEdit || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        openStudio();
      }}
      role={canEdit ? "link" : undefined}
      tabIndex={canEdit ? 0 : undefined}
      aria-label={canEdit ? `Open ${workflowName} in workflow studio` : undefined}
      className={cn(
        "agent-card group flex min-h-64 flex-col p-4 sm:p-5 transition-all hover:-translate-y-0.5 hover:shadow-card-hover shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/30",
        canEdit ? "cursor-pointer" : "cursor-default"
      )}
    >
      <div>
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400 font-semibold text-sm">
            <WorkflowIcon className="h-5 w-5" />
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <h3 className="min-w-0 flex-1 truncate text-base font-bold tracking-tight text-ink" title={workflowName}>
                {displayWorkflowName}
              </h3>
              <span
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                  isPublished ? "bg-success-soft text-success" : "bg-soft text-ink-2",
                )}
              >
                <span className={cn("h-1.5 w-1.5 rounded-full", isPublished ? "bg-success" : "bg-warning")} />
                {isPublished ? "Published" : "Draft"}
              </span>
              <div onClick={(event) => event.stopPropagation()}>
                <ActionMenu ariaLabel={`${workflowName} actions`} items={[
                  { label: "Open Studio", onClick: openStudio },
                  {
                    label: "View Runs",
                    icon: <Play className="h-4 w-4" />,
                    onClick: () => navigate(`/home/automation/runs?workflowId=${workflow.id}`),
                  },
                  ...(isPublished ? [{
                    label: "Schedule",
                    icon: <Calendar className="h-4 w-4" />,
                    onClick: () => navigate(`/home/automation/schedule?workflowId=${workflow.id}`),
                  }] : []),
                  {
                    label: "Delete Workflow",
                    icon: <Trash2 className="h-4 w-4" />,
                    danger: true,
                    onClick: () => onDelete(workflow),
                  },
                ]} />
              </div>
            </div>
            <p className="mt-1 font-mono text-[10px] text-ink-3">
              {stepCount} {stepCount === 1 ? "step" : "steps"} · v{workflow.workflow_version || 1}
            </p>
          </div>
        </div>

        <p className="mt-4 line-clamp-2 min-h-10 text-sm leading-5 text-ink-2">
          {workflow.description ||
            "Multi-step voice agent automation sequence with conditions and delays"}
        </p>

        <div className="mt-4 flex min-h-7 flex-wrap items-center gap-1.5">
          <span className="mr-1 text-xs font-medium text-ink-3">
            Channels
          </span>
          {(channels.length ? channels : [{ type: "call", ...CHANNEL_DEFS.call }]).map((channel) => {
            const Icon = channel.icon;
            return (
              <span key={channel.type} className="inline-flex items-center gap-1 rounded-md bg-soft px-2 py-1 text-[11px] font-medium text-ink-2">
                <Icon className="h-3 w-3 text-ink-3" />
                {channel.label}
              </span>
            );
          })}
        </div>
      </div>

      <div
        className="mt-5 flex flex-wrap items-center gap-2 border-t border-line pt-4"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          onClick={openStudio}
          className="rounded-lg bg-ink px-3.5 py-2 text-xs font-semibold text-canvas transition-opacity hover:opacity-90"
        >
          Open studio
        </button>
        {isPublished && (
          <button
            onClick={() => navigate(`/home/automation/schedule?workflowId=${workflow.id}`)}
            className="flex items-center gap-1.5 rounded-lg bg-brand-soft px-3 py-2 text-xs font-semibold text-brand transition-colors hover:bg-brand hover:text-white"
          >
            <Calendar className="h-3.5 w-3.5" /> Schedule
          </button>
        )}
        <button
          onClick={() => navigate(`/home/automation/runs?workflowId=${workflow.id}`)}
          className="ml-auto flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold text-ink-2 transition-colors hover:bg-soft hover:text-ink"
        >
          <Play className="h-3.5 w-3.5" /> Runs
        </button>
      </div>
    </article>
  );
}
